import type {
  MeetingNoteOut,
  MeetingNotesOut,
  NoteOutlineNodeOut,
  NoteSectionOut,
} from "../contract/api";
import { STRINGS, t, type StringKey } from "../contract/strings";

/**
 * The Detailed tab's notes in a style (#774), as pure rows.
 *
 * The same rows the Rust core's `mf-present` `notes_rows` produces for the apps, from the
 * same shared fixtures (`contract/fixtures/present/notes/`), checked field for field by
 * `engine/present.test.ts`. Names are already the reader's current ones -- the server
 * fills them in -- and every sentence is a message key with its placeholders.
 */

export interface NotesMessage {
  key: string;
  params: Record<string, string>;
  nested: Record<string, { key: string; params: Record<string, string> }>;
}

/**
 * A style's state, the one its chip, its status line and its buttons follow:
 * `idle` (nobody asked), `writing` (the first notes), `updating` (new notes, the old ones
 * still shown), `ready`, `out_of_date` (the meeting was processed again since), `failed`
 * (the first notes could not be written) and `update_failed` (the old notes stay).
 */
export type NoteState =
  | "idle"
  | "writing"
  | "updating"
  | "ready"
  | "out_of_date"
  | "failed"
  | "update_failed";

export interface StyleChipRow {
  key: string;
  label: NotesMessage;
  /** The server's English name, for a key this build's strings do not have. */
  fallback: string;
  default: boolean;
  state: NoteState;
  /** Notes have been written in this style: the chip shows a tick. */
  written: boolean;
  /**
   * How the style shows the meeting's actions: the result's commitments, Quick's own
   * list and ticks (#774) -- `list`, `by_person`, or `null` for none. Notes never
   * carry actions of their own.
   */
  commitments: "list" | "by_person" | null;
}

export interface NoteQuoteRow {
  speaker: string;
  text: string;
  source_start: number;
}

export interface NoteSectionRow {
  key: string;
  heading: string;
  body: string | null;
  bullets: string[];
  quotes: NoteQuoteRow[];
}

/** One mind-map node, flattened depth first; `id` is its path (`0`, `0.1`, `0.1.2`). */
export interface OutlineRow {
  id: string;
  parent: string | null;
  depth: number;
  label: string;
  has_children: boolean;
}

export interface NoteRows {
  style: string;
  state: NoteState;
  status: NotesMessage | null;
  can_update: boolean;
  can_retry: boolean;
  polling: boolean;
  language: string | null;
  sections: NoteSectionRow[];
  outline: OutlineRow[];
}

export interface NotesRows {
  enabled: boolean;
  chips: StyleChipRow[];
  notes: NoteRows[];
}

function message(key: string, params: Record<string, string> = {}): NotesMessage {
  return { key, params, nested: {} };
}

export function noteState(note: MeetingNoteOut): NoteState {
  const hasContent = note.content != null;
  if (note.status === "writing") return hasContent ? "updating" : "writing";
  if (note.status === "failed") return hasContent ? "update_failed" : "failed";
  return note.out_of_date ? "out_of_date" : "ready";
}

const STATUS: Partial<Record<NoteState, string>> = {
  writing: "result.notes_writing",
  updating: "result.notes_updating",
  failed: "result.notes_failed",
  update_failed: "result.notes_update_failed",
  out_of_date: "result.notes_out_of_date",
};

function sectionRow(section: NoteSectionOut): NoteSectionRow {
  return {
    key: section.key,
    heading: section.heading,
    body: section.body ? section.body : null,
    bullets: (section.bullets ?? []).filter((bullet) => bullet !== ""),
    quotes: (section.quotes ?? []).map((quote) => ({
      speaker: quote.speaker,
      text: quote.text,
      source_start: quote.source_start,
    })),
  };
}

function outlineRows(
  nodes: NoteOutlineNodeOut[],
  parent: string | null,
  depth: number,
  rows: OutlineRow[],
): void {
  if (depth > 3) return;
  nodes.forEach((node, index) => {
    const id = parent === null ? String(index) : `${parent}.${index}`;
    const children = node.children ?? [];
    rows.push({ id, parent, depth, label: node.label, has_children: depth < 3 && children.length > 0 });
    outlineRows(children, id, depth + 1, rows);
  });
}

function noteRows(note: MeetingNoteOut): NoteRows {
  const state = noteState(note);
  const outOfDate = note.out_of_date === true;
  const content = note.content ?? null;
  const outline: OutlineRow[] = [];
  if (content) outlineRows(content.outline ?? [], null, 1, outline);
  const key = STATUS[state];
  return {
    style: note.style,
    state,
    status: key ? message(key) : null,
    can_update: outOfDate && (state === "out_of_date" || state === "update_failed"),
    can_retry: state === "failed" || (state === "update_failed" && !outOfDate),
    polling: state === "writing" || state === "updating",
    language: content?.language ? content.language : null,
    sections: content ? content.sections.map(sectionRow) : [],
    outline,
  };
}

/** The Detailed tab's rows (the Rust core's `notes_rows`). */
export function notesRows(out: MeetingNotesOut): NotesRows {
  return {
    enabled: out.enabled,
    chips: out.styles.map((style) => {
      const note = out.notes.find((n) => n.style === style.key);
      return {
        key: style.key,
        label: message(style.label_key),
        fallback: style.label,
        default: style.default ?? false,
        state: note ? noteState(note) : "idle",
        written: note ? note.content != null : false,
        commitments: style.commitments ?? null,
      };
    }),
    notes: out.notes.map(noteRows),
  };
}

/** Render a row's message with the shipped strings. */
export function say(msg: NotesMessage): string {
  return t(msg.key as StringKey, msg.params);
}

/** A style's name: this build's copy for its key, else the server's own name. */
export function styleLabel(chip: Pick<StyleChipRow, "label" | "fallback">): string {
  return chip.label.key in STRINGS ? say(chip.label) : chip.fallback;
}

/**
 * The style to show when the tab opens with none in the address: the default style once
 * it has notes or is being written, else the first style that has any; `null` (pick one)
 * when nobody has asked for notes yet. Never starts one: writing costs a model call, so it
 * is the reader's tap.
 */
export function initialStyle(rows: NotesRows): string | null {
  const asked = rows.chips.filter((chip) => chip.state !== "idle");
  return (asked.find((chip) => chip.default) ?? asked[0])?.key ?? null;
}

/** Children of each mind-map node, by parent id (`""` for the top level). */
export function outlineChildren(rows: OutlineRow[]): Map<string, OutlineRow[]> {
  const children = new Map<string, OutlineRow[]>();
  for (const row of rows) {
    const key = row.parent ?? "";
    children.set(key, [...(children.get(key) ?? []), row]);
  }
  return children;
}

/**
 * The tree items a keyboard moves through, top to bottom: every node whose ancestors are
 * all expanded.
 */
export function visibleOutline(rows: OutlineRow[], expanded: Set<string>): OutlineRow[] {
  const shown = new Set<string>();
  return rows.filter((row) => {
    const visible = row.parent === null || (shown.has(row.parent) && expanded.has(row.parent));
    if (visible) shown.add(row.id);
    return visible;
  });
}

/**
 * The notes as lines of the exported text file: per written style, its name, then each
 * section's heading, body, bullets and quotes. The meeting's actions are in the file
 * once, as the next steps above the notes: the notes have none of their own.
 */
export function notesExportLines(out: MeetingNotesOut | null): string[] {
  if (!out) return [];
  const rows = notesRows(out);
  const lines: string[] = [];
  for (const chip of rows.chips) {
    const note = rows.notes.find((n) => n.style === chip.key);
    if (!note || !note.sections.length) continue;
    lines.push(t("result.export_notes_heading", { style: styleLabel(chip) }));
    for (const section of note.sections) {
      if (section.heading) lines.push(`### ${section.heading}`);
      if (section.body) lines.push(section.body);
      for (const bullet of section.bullets) lines.push(`- ${bullet}`);
      for (const quote of section.quotes) lines.push(`> “${quote.text}” (${quote.speaker})`);
      lines.push("");
    }
  }
  return lines;
}
